import { useQuery } from "@tanstack/solid-query";
import { createMemo } from "solid-js";
import { axiosInstance } from "@/api/client";
import { ONBOARDING_SKILL_CHIPS_KEY } from "@/api/query-keys";
import { isJobCategory } from "@/domains/job-offer/create/constants";

const SKILL_CHIPS_PATH = "/v1/profile/onboarding/suggested-skills";

// Il BE ne manda già al più 12 (§5.2): il taglio tiene lo step uguale anche se
// il contratto si allarga (S7.1).
const MAX_SKILL_CHIPS = 12;

type OnboardingSkillChipsResponse = {
  success: boolean;
  data: { job_category: string; skills: string[] };
};

// 1. Fetcher: toglie la busta `{ success, data }`. Nessuna gestione degli
// errori qui: a un errore lo step resta senza chip (S7.3).
export const fetchOnboardingSkillChips = async (
  category: string,
  signal?: AbortSignal,
): Promise<string[]> => {
  const { data } = await axiosInstance.get<OnboardingSkillChipsResponse>(
    SKILL_CHIPS_PATH,
    { params: { job_category: category }, signal },
  );
  return data.data.skills.slice(0, MAX_SKILL_CHIPS);
};

// 2. Opzioni della query, una chiave per categoria.
export const createOnboardingSkillChipsQueryOptions = (
  category: string | null | undefined,
) => ({
  queryKey: [ONBOARDING_SKILL_CHIPS_KEY, category],
  // Senza una delle 8 categorie (vuota o tolta) la query resta ferma, quindi
  // il queryFn non parte mai con una stringa vuota né con una categoria tolta
  // (taxonomy-contract-pr3 D9).
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchOnboardingSkillChips(category ?? "", signal),
  enabled: isJobCategory(category),
  // Quanto la cache del BE: tornando a una categoria già vista i chip ci sono
  // subito.
  staleTime: 5 * 60_000,
  // I chip sono un aiuto, non un blocco: a un errore lo step prosegue senza,
  // invece di aspettare i tentativi (P-DL15).
  retry: false,
});

/**
 * I chip da mostrare: solo a query riuscita. `isSuccess` va letto **prima** di
 * `data`, perché in solid-query leggere `data` durante la prima fetch sospende,
 * e lo step lascerebbe il posto al fallback di app. In caricamento, in errore
 * o senza categoria, nessun chip e nessuna lista di riserva (S7.3).
 */
export const skillChipsOf = (query: {
  isSuccess: boolean;
  data: string[] | undefined;
}): string[] => (query.isSuccess ? (query.data ?? []) : []);

// 3. Hook: la categoria è un accessor, così cambiandola cambia la chiave e
// arrivano i chip della nuova (S7.2).
export const useOnboardingSkillChips = (
  category: () => string | null | undefined,
) => {
  const query = useQuery(() =>
    createOnboardingSkillChipsQueryOptions(category()),
  );
  return createMemo(() => skillChipsOf(query));
};

const chipKey = (chip: string) => chip.trim().toLowerCase();

/** La lista senza ripetizioni (maiuscole e spazi ai bordi ignorati), nell'ordine. */
const uniqueChips = (chips: string[]) => {
  const seen = new Set<string>();
  return chips.filter((chip) => {
    const key = chipKey(chip);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Fonde i chip dei due settori (D11, AC18). Il BE ne manda fino a `cap` per
 * settore: tagliare il principale al cap farebbe sparire il secondario, quindi
 * con entrambe le liste piene il cap si divide. Nell'ordine:
 * 1. fino a `ceil(cap / 2)` chip del principale;
 * 2. dal secondario, quelli non presenti nel principale, fino al cap;
 * 3. se restano posti, il resto del principale, poi il resto del secondario.
 * Il risultato è principale, secondario, riempitivi: il principale resta per
 * primo e i due settori sono visibili. Con una lista vuota è l'altra, al cap.
 *
 * Il confronto ignora maiuscole e spazi ai bordi ("React" = "react") e resta la
 * grafia della prima occorrenza. Un chip presente nelle due liste appartiene al
 * principale: il secondario lo salta, così il suo posto va a un chip nuovo.
 */
export const mergeSkillChips = (
  primary: string[],
  secondary: string[],
  cap = MAX_SKILL_CHIPS,
): string[] => {
  const first = uniqueChips(primary);
  const primaryKeys = new Set(first.map(chipKey));
  const second = uniqueChips(secondary).filter(
    (chip) => !primaryKeys.has(chipKey(chip)),
  );
  if (first.length === 0) return second.slice(0, cap);
  if (second.length === 0) return first.slice(0, cap);

  const primaryShare = Math.ceil(cap / 2);
  const primaryPicks = first.slice(0, primaryShare);
  const secondaryPicks = second.slice(0, cap - primaryPicks.length);
  const fillers = [
    ...first.slice(primaryPicks.length),
    ...second.slice(secondaryPicks.length),
  ];
  return [...primaryPicks, ...secondaryPicks, ...fillers].slice(0, cap);
};

// 4. Hook a due settori: `[principale, secondario]`. Sono due chiamate del
// hook a categoria singola, quindi nessun endpoint o parametro nuovo, una
// chiave per categoria (cambiare settore non tocca le altre) e nessuna
// richiesta per `null`.
export const useOnboardingSkillChipsFor = (
  categories: () => (string | null | undefined)[],
) => {
  const primary = useOnboardingSkillChips(() => categories()[0]);
  const secondary = useOnboardingSkillChips(() => categories()[1]);
  return createMemo(() => mergeSkillChips(primary(), secondary()));
};
